import { useState, useRef, type DragEvent } from 'react'
import {
  Stack, Paper, Group, Text, ActionIcon, Badge,
  Progress, Alert, FileButton, Button, Box,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconUpload, IconFile, IconTrash, IconAlertCircle,
  IconCheck, IconX, IconCloudUpload,
} from '@tabler/icons-react'
import { validateFile } from '@/lib/attachmentMime'

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'failed'

export interface BufferedFile {
  file: File
  status: UploadStatus
  progress: number
  error?: string
}

interface Props {
  files: BufferedFile[]
  onChange: (files: BufferedFile[]) => void
  required?: boolean
  disabled?: boolean
  maxFiles?: number
}

function humanizeSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileStatusBadge({ status }: { status: UploadStatus }) {
  if (status === 'pending') return <Badge color="gray" size="sm">Pendente</Badge>
  if (status === 'uploading') return <Badge color="blue" size="sm">Enviando…</Badge>
  if (status === 'done')
    return (
      <Badge color="green" size="sm" leftSection={<IconCheck size={10} />}>
        Enviado
      </Badge>
    )
  return (
    <Badge color="red" size="sm" leftSection={<IconX size={10} />}>
      Falhou
    </Badge>
  )
}

export function EvidenceBuffer({
  files,
  onChange,
  required = false,
  disabled = false,
  maxFiles = 10,
}: Props) {
  const handleAdd = (selected: File[] | null) => {
    if (!selected || selected.length === 0) return
    const newOnes: BufferedFile[] = []
    for (const f of selected) {
      if (files.length + newOnes.length >= maxFiles) {
        notifications.show({
          color: 'red',
          title: 'Limite de arquivos',
          message: `Máximo ${maxFiles} evidências por chamado`,
        })
        break
      }
      const err = validateFile(f)
      if (err) {
        notifications.show({ color: 'red', title: 'Arquivo rejeitado', message: `${f.name}: ${err}` })
        continue
      }
      newOnes.push({ file: f, status: 'pending', progress: 0 })
    }
    if (newOnes.length > 0) onChange([...files, ...newOnes])
  }

  const handleRemove = (index: number) => {
    onChange(files.filter((_, i) => i !== index))
  }

  const totalSize = files.reduce((acc, f) => acc + f.file.size, 0)

  // TASK-2561 MR-E: dropzone nativo HTML5 — sem dep extra.
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    dragCounter.current += 1
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)
    if (disabled) return
    const dropped = Array.from(e.dataTransfer.files ?? [])
    if (dropped.length > 0) handleAdd(dropped)
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <Text fw={500} size="sm">
            Evidências{' '}
            <Text component="span" c={required ? 'red' : 'dimmed'} size="xs">
              {required ? '(obrigatório)' : '(opcional)'}
            </Text>
          </Text>
          {files.length > 0 && (
            <Text size="xs" c="dimmed">
              {files.length} arquivo{files.length === 1 ? '' : 's'} • {humanizeSize(totalSize)}
            </Text>
          )}
        </Group>
        <FileButton onChange={handleAdd} multiple disabled={disabled || files.length >= maxFiles}>
          {(props) => (
            <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}>
              Adicionar evidência
            </Button>
          )}
        </FileButton>
      </Group>

      {/* Dropzone visual — drag-and-drop nativo (sem @mantine/dropzone) */}
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        role="presentation"
        aria-label="Área para arrastar arquivos"
        style={{
          border: `2px dashed ${isDragging ? 'var(--mantine-color-inovareTeal-6)' : 'var(--mantine-color-gray-3)'}`,
          borderRadius: 8,
          padding: '12px 16px',
          textAlign: 'center',
          background: isDragging ? 'var(--mantine-color-inovareTeal-0)' : 'transparent',
          transition: 'all 120ms ease',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Group gap="xs" justify="center" align="center">
          <IconCloudUpload size={20} color={isDragging ? 'var(--mantine-color-inovareTeal-6)' : 'var(--mantine-color-gray-6)'} />
          <Text size="xs" c={isDragging ? 'inovareTeal.7' : 'dimmed'}>
            {isDragging
              ? 'Solte aqui para anexar'
              : 'Arraste arquivos aqui ou clique em [+ Adicionar evidência]'}
          </Text>
        </Group>
      </Box>

      {required && files.length === 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" py="xs">
          <Text size="xs">Esta categoria exige ao menos uma evidência para criação.</Text>
        </Alert>
      )}

      {files.map((bf, idx) => (
        <Paper key={`${bf.file.name}-${idx}`} p="xs" withBorder>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <IconFile size={20} />
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" truncate>
                  {bf.file.name}
                </Text>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    {humanizeSize(bf.file.size)}
                  </Text>
                  <FileStatusBadge status={bf.status} />
                </Group>
                {bf.status === 'uploading' && (
                  <Group gap="xs" align="center">
                    <Progress value={bf.progress} size="xs" style={{ flex: 1 }} />
                    <Text size="xs" c="dimmed">
                      {bf.progress}%
                    </Text>
                  </Group>
                )}
                {bf.status === 'failed' && bf.error && (
                  <Text size="xs" c="red">
                    {bf.error}
                  </Text>
                )}
              </Stack>
            </Group>
            {bf.status !== 'uploading' && !disabled && (
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                aria-label="Remover evidência"
                onClick={() => handleRemove(idx)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            )}
          </Group>
        </Paper>
      ))}
    </Stack>
  )
}
